/**
 * Public community leaderboard widget — auto-rotating top contributors.
 *
 * Fetches the top 10, then animates through 5-at-a-time pages every
 * ~5 seconds with a fade transition (pauses on hover). Always shows
 * 🏆🥈🥉 medals for ranks 1-3 of the FULL list so a contributor's
 * position never flips arbitrarily between pages.
 *
 * Real post counts are pulled from the backend; the chip is hidden
 * when count is 0 so we never render "· 0 posts".
 */
import { useEffect, useMemo, useState } from "react";
import { Trophy, Award, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const PAGE_SIZE = 5;
const ROTATE_MS = 5500;

const MEDAL_BG = ["bg-amber-400 text-amber-950", "bg-slate-300 text-slate-900", "bg-orange-300 text-orange-950"];
const MEDAL_EMOJI = ["🏆", "🥈", "🥉"];

export default function CommunityLeaderboardWidget({ limit = 10, scope = "all", compact = false, emptyOnly = false }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);

  // Fetch once.
  useEffect(() => {
    // When the parent has forced the empty-CTA layout we never fetch.
    // Matches the homepage Testimonials section where the user wants a
    // simple "Be the first on the podium" prompt instead of the live
    // leaderboard rotation.
    if (emptyOnly) { setItems([]); return; }
    const ctrl = new AbortController();
    fetch(`${API}/api/community/leaderboard-v2?limit=${limit}${scope !== "all" ? `&scope=${scope}` : ""}`, { signal: ctrl.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setItems(d.items || []))
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setError(true);
      });
    return () => ctrl.abort();
  }, [limit, scope, emptyOnly]);

  // Group into pages of 5. Each contributor keeps their global rank.
  const pages = useMemo(() => {
    if (!items?.length) return [];
    const out = [];
    for (let i = 0; i < items.length; i += PAGE_SIZE) {
      out.push(items.slice(i, i + PAGE_SIZE).map((it, j) => ({ ...it, _rank: i + j })));
    }
    return out;
  }, [items]);

  // Auto-rotate. Uses a self-rescheduling timeout instead of
  // setInterval so React's strict-mode double-mount cleanup can't
  // strand a stale handle.
  useEffect(() => {
    if (paused || pages.length <= 1) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setFading(true);
      setTimeout(() => {
        if (cancelled) return;
        setPageIdx((p) => (p + 1) % pages.length);
        setFading(false);
        setTimeout(tick, ROTATE_MS);
      }, 280);
    };
    const handle = setTimeout(tick, ROTATE_MS);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [paused, pages.length]);

  if (error || (items && items.length === 0)) {
    return (
      <aside data-testid="community-leaderboard-empty" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-emerald-700"/>
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Top contributors this month</span>
        </div>
        <p className="text-sm text-slate-700 mb-3">Be the first to land on the podium — post a vendor recommendation or ask the community a question.</p>
        <Link to="/community" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800">Join the community <ArrowRight className="w-3 h-3"/></Link>
      </aside>
    );
  }

  if (!items) {
    return (
      <aside className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse">
        <div className="h-3 w-32 bg-slate-100 rounded mb-3"/>
        <div className="space-y-2">{[0,1,2].map(i => <div key={i} className="h-5 bg-slate-100 rounded"/>)}</div>
      </aside>
    );
  }

  const current = pages[pageIdx] || [];

  return (
    <aside
      data-testid="community-leaderboard"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={`rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white shadow-sm overflow-hidden ${compact ? "p-4" : "p-5"}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-emerald-700"/>
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Top contributors this month</span>
        </div>
        <Link to="/community" className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-0.5">See all <ArrowRight className="w-3 h-3"/></Link>
      </div>

      <ol className={`space-y-2 transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`} data-testid="leaderboard-list" key={pageIdx}>
        {current.map((it) => {
          const rank = it._rank;
          return (
            <li key={it.user_id} data-testid={`leader-${rank}`} className="flex items-center gap-3 py-1.5">
              <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${rank < 3 ? MEDAL_BG[rank] : "bg-slate-100 text-slate-700"}`}>
                {rank < 3 ? MEDAL_EMOJI[rank] : rank + 1}
              </span>
              <div className="min-w-0 flex-1">
                {/* Show the display_name when the persona has opted in
                    (pseudonym_ok=true on the backend); otherwise fall back
                    to "Landlord in ON" anonymous label. Real users default
                    to anonymous unless they explicitly opt in to surface
                    their name on the public board. */}
                <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900 truncate">
                  <span className="truncate" data-testid={`leader-label-${rank}`}>
                    {it.anonymized
                      ? `${it.role === "landlord" ? "Landlord" : it.role === "tenant" ? "Tenant" : "Member"}${it.province ? ` in ${it.province}` : ""}`
                      : (it.display_name || "Member")}
                  </span>
                  {it.top_contributor && <Star className="w-3 h-3 text-amber-500 fill-current shrink-0" title="Top Contributor badge"/>}
                </div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                  {it.anonymized
                    ? <span className="uppercase tracking-wide font-bold">Anonymous</span>
                    : <span className="uppercase tracking-wide font-bold">{it.role}{it.province ? ` · ${it.province}` : ""}</span>}
                  {(it.post_count || 0) > 0 && <span>· {it.post_count} post{it.post_count === 1 ? "" : "s"}</span>}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="font-display text-lg font-extrabold text-slate-900 tabular-nums" data-testid={`leader-points-${rank}`}>{it.points}</div>
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 leading-none">pts</div>
              </div>
            </li>
          );
        })}
      </ol>

      {pages.length > 1 && (
        <div className="mt-3 flex items-center gap-1.5" data-testid="leaderboard-dots">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => { setFading(true); setTimeout(() => { setPageIdx(i); setFading(false); }, 200); }}
              aria-label={`Show page ${i + 1}`}
              data-testid={`leaderboard-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === pageIdx ? "w-6 bg-emerald-600" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`}
            />
          ))}
          <span className="ml-auto text-[10px] uppercase tracking-wide font-bold text-slate-400">{pageIdx * PAGE_SIZE + 1}-{Math.min((pageIdx + 1) * PAGE_SIZE, items.length)} of {items.length}</span>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center gap-2 text-[11px] text-emerald-800">
        <Award className="w-3 h-3"/> Earn points by helping fellow Canadian landlords + tenants — every post +5, every helpful comment +3.
      </div>
    </aside>
  );
}
